import moment from 'moment';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession
} from 'amazon-cognito-identity-js';
import type {ICognitoUserPoolData} from 'amazon-cognito-identity-js';
import * as requestFromServer from './authCrud';
import {actions} from './authRedux';
import {AppDispatch, persistor} from '../../../../redux/store';
import poolConfig from '../../../../config/cognito.json';
import config from '../../../../config/common.json';

const userPool = new CognitoUserPool(poolConfig as unknown as ICognitoUserPoolData);

export const login = ({username, password}: {
  username: string;
  password: string;
}) => (dispatch: AppDispatch) => {
  const authenticationData = {
    Username: username,
    Password: password,
  };
  const authenticationDetails = new AuthenticationDetails(authenticationData);
  const userData = {
    Username: username,
    Pool: userPool,
  };
  const cognitoUser = new CognitoUser(userData);
  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: function (userSession) {
        const tokens = extractUserSessionTokens(userSession);
        cognitoUser.getUserAttributes((err, result) => {
          if (err) return console.error(err.message || JSON.stringify(err));
          const attributes = transformUserAttributes(result || []);
          resolve(requestFromServer
            .loginWithTokens(
              attributes.email,
              tokens.accessToken,
              tokens.idToken,
              tokens.refreshToken,
              tokens.expire
            )
            .then(response => {
              dispatch(actions.login(
                {...tokens},
                {...cognitoUser, attributes},
                response.data.body.greenInvoiceApiKey,
                response.data.body.greenInvoiceSecret,
                response.data.body.greenInvoicePaymentPluginID,
                (Boolean(response.data.body.pages))
                  ? response.data.body.pages.map(page => {
                    return {
                      pageName: page.page_name,
                      permissionLevel: page.permission_level,
                      default: Boolean(page.is_default)
                    };
                  })
                  : [],
                response.data.body.adminPermissionsLevel,
                moment(response.data.header.response_server_time).toDate()
              ));
            })
            .catch(err => console.error(err.message || JSON.stringify(err)))
          );
        });
      },
      onFailure: function (err) {
        const errText = err.message || JSON.stringify(err);
        console.error(errText);
        reject(errText);
      },
    });
  });
};

async function logoutFromServer() {
  try {
    await requestFromServer.logout();
  } catch (err: any) {
    console.error(err?.message || JSON.stringify(err));
  }
}

export const logout = () => async (dispatch: AppDispatch) => {
  try {
    await logoutFromServer();
    await persistor.purge();
  } catch (err: any) {
    console.error(err?.message || JSON.stringify(err));
  } finally {
    dispatch(actions.logout());
  }
};

export const refreshToken = (accessToken: string) => async (dispatch: AppDispatch) => {
  const response = await requestFromServer.refreshToken(accessToken);
  const {errorCode, header, body} = response.data;
  if (errorCode < 0) throw new Error();
  dispatch(actions.registerToken({
    accessToken: body.newTokenData.accessToken,
    expire: moment().add(config.expireTimeout).unix(),
    receivedAt: moment(header.response_server_time).toDate(),
  }));

  return body.newTokenData.accessToken;
};

export const getUserPermissions = () => async (dispatch: AppDispatch) => {
  const response = await requestFromServer
    .getUserPermissions();
  const {errorCode, body} = response.data;
  if (errorCode < 0) throw new Error();
  const permissions = body.permissions;
  return dispatch(actions.updatePermissions(permissions));
};

const extractUserSessionTokens = (sessionInfo: CognitoUserSession) => {
  const accessTokenData = sessionInfo.getAccessToken();
  const refreshTokenData = sessionInfo.getRefreshToken();
  const idTokenData = sessionInfo.getIdToken();

  return {
    uid: accessTokenData,
    accessToken: accessTokenData.getJwtToken(),
    idToken: idTokenData.getJwtToken(),
    refreshToken: refreshTokenData.getToken(),
    expire: accessTokenData.getExpiration()
  };
};

const transformUserAttributes = (cognitoAttributes: CognitoUserAttribute[]) => {
  return cognitoAttributes.reduce((acc, attr) => {
    const {Name, Value} = attr;
    return {...acc, [Name]: Value};
  }, {
    sub: '',
    email_verified: '',
    name: '',
    'custom:licence': '',
    family_name: '',
    email: '',
  });
};
